import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import { useDispatch, useSelector } from "react-redux";
import { Formik } from "formik";
import Button from "@mui/material/Button";
import * as yup from "yup";
import { useNavigate } from 'react-router-dom';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { toast } from 'react-toastify';
import Box from '@mui/material/Box';
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AppBar from '@mui/material/AppBar';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import Container from "../../GeneralComponents/Container";
import { Topbar, Sidebar } from '../components';
import setGlobalLoader from '../../../redux/actions/loaderActions';
import { getOrganizations, createStudent } from '../../../redux/actions/apiActions';
import { PROFILES, BLOOD_GROUPS } from '../../../utils/constants';
import { statesAndCities } from '../../../utils/indianStatesCities';
import { s3Upload } from '../../../utils/uploadService';
import { compressImageWithDimension } from '../../../utils/imageCompressor';
import { CircularProgress } from '@mui/material';
import { ObjectId } from 'bson';

const BLOOD_GROUP_OPTIONS = Object.values(BLOOD_GROUPS).map(bg => ({
    value: bg,
    label: bg,
}));

const validationSchema = yup.object({
    name: yup
        .string()
        .trim()
        .required("Name is required."),
    phoneNumber: yup
        .string()
        .trim()
        .required("Phone Number is required.")
        .min(10, "Phone number short than 10 digits.")
        .max(10, "Phone number long than 10 digits."),
    email: yup
        .string()
        .email("Invalid email address.")
        .nullable(),
    guardianPhoneNumber: yup
        .string()
        .trim()
        .min(10, "Phone number short than 10 digits.")
        .max(10, "Phone number long than 10 digits.")
        .nullable(),
    dob: yup
        .string()
        .nullable(),
    bloodGroup: yup
        .string()
        .nullable(),
})

const AddStudent = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [selectedOrganization, setSelectedOrganization] = useState({});
    const [selectedBloodGroup, setSelectedBloodGroup] = useState(null);
    const [organizations, setOrganizations] = useState([]);
    const profile = useSelector(state => state.authReducer.profile);
    const user = useSelector(state => state.authReducer.user);
    const theme = useTheme();
    const isMd = useMediaQuery(theme.breakpoints.up('md'), {
        defaultMatches: true,
    });

    // State and City dropdowns
    const [selectedState, setSelectedState] = useState(null);
    const [stateOptions, setStateOptions] = useState([]);
    const [selectedCity, setSelectedCity] = useState(null);
    const [cityOptions, setCityOptions] = useState([]);

    // File upload states
    const [profileImgFile, setProfileImgFile] = useState(null);
    const [profileImgPreview, setProfileImgPreview] = useState(null);
    const [learningLicenseImgFile, setLearningLicenseImgFile] = useState(null);
    const [learningLicenseImgPreview, setLearningLicenseImgPreview] = useState(null);
    const [addressProofImgFile, setAddressProofImgFile] = useState(null);
    const [addressProofImgPreview, setAddressProofImgPreview] = useState(null);
    const profileImgInputRef = useRef(null);
    const learningLicenseInputRef = useRef(null);
    const addressProofInputRef = useRef(null);

    // Documents state
    const [documents, setDocuments] = useState([]);
    const [newDocName, setNewDocName] = useState('');
    const [newDocFile, setNewDocFile] = useState(null);
    const [newDocPreview, setNewDocPreview] = useState(null);
    const documentInputRef = useRef(null);

    // Image preview dialog
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');

    const [openSidebar, setOpenSidebar] = useState(false);

    const handleSidebarOpen = () => {
        setOpenSidebar(true);
    };

    const handleSidebarClose = () => {
        setOpenSidebar(false);
    };
    const open = isMd ? false : openSidebar;

    // Initialize state options
    useEffect(() => {
        setStateOptions(statesAndCities.map(item => ({ label: item.state, value: item.state })));
    }, []);

    // Update city options when state changes
    useEffect(() => {
        if (selectedState) {
            const stateData = statesAndCities.find(s => s.state.toLowerCase() === selectedState.value.toLowerCase());
            if (stateData) {
                const cities = [...stateData.cities].sort((a, b) => a.localeCompare(b));
                setCityOptions(cities.map(city => ({ label: city, value: city })));
            }
        } else {
            setCityOptions([]);
        }
        setSelectedCity(null);
    }, [selectedState]);

    useEffect(() => {
        if (profile === PROFILES.ADMIN) {
            dispatch(setGlobalLoader(true))
            async function organizationFunc() {
                const query = '?limit=10000&page=1';
                try {
                    const orgRes = await getOrganizations(query);
                    const data = orgRes?.data?.data?.docs?.map((d) => ({
                        label: d?.name,
                        value: d?._id,
                    }))
                    setOrganizations(data);
                    dispatch(setGlobalLoader(false))
                } catch (error) {
                    dispatch(setGlobalLoader(false))
                    console.log("get Organizations error", error);
                    toast.error(error.response.data.msg ?? "some error occured while fetching organization list");
                }
            }
            organizationFunc();
        }
    }, [])

    const handleImageSelect = (event, type) => {
        const file = event.target.files[0];
        if (!file) return;

        const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            toast.error('Please upload a valid image file (JPEG, PNG, or WebP)');
            return;
        }

        const maxSize = 5;
        if (file.size > maxSize * 1024 * 1024) {
            toast.error(`Image size should be less than ${maxSize}MB`);
            return;
        }

        const previewUrl = URL.createObjectURL(file);

        if (type === 'profile') {
            setProfileImgFile(file);
            setProfileImgPreview(previewUrl);
        } else if (type === 'learningLicense') {
            setLearningLicenseImgFile(file);
            setLearningLicenseImgPreview(previewUrl);
        } else if (type === 'addressProof') {
            setAddressProofImgFile(file);
            setAddressProofImgPreview(previewUrl);
        } else if (type === 'document') {
            setNewDocFile(file);
            setNewDocPreview(previewUrl);
        }
    };

    const handleRemoveImage = (type) => {
        if (type === 'profile') {
            setProfileImgFile(null);
            setProfileImgPreview(null);
        } else if (type === 'learningLicense') {
            setLearningLicenseImgFile(null);
            setLearningLicenseImgPreview(null);
        } else if (type === 'addressProof') {
            setAddressProofImgFile(null);
            setAddressProofImgPreview(null);
        }
    };

    const handleAddDocument = () => {
        if (newDocName.trim() && newDocFile) {
            setDocuments([...documents, {
                name: newDocName.trim(),
                file: newDocFile,
                preview: newDocPreview
            }]);
            setNewDocName('');
            setNewDocFile(null);
            setNewDocPreview(null);
            // Reset file input so the same file can be selected again
            if (documentInputRef.current) {
                documentInputRef.current.value = '';
            }
        } else {
            toast.error('Please enter document name and select a file');
        }
    };

    const handleRemoveDocument = (index) => {
        setDocuments(documents.filter((_, i) => i !== index));
    };

    const openImagePreview = (imageUrl) => {
        setPreviewImage(imageUrl);
        setPreviewOpen(true);
    };

    const handleOnSubmit = async (values) => {
        if (values.organizationId === "" && profile === PROFILES.ADMIN) {
            toast.error("Please select an organization.");
            return;
        }

        dispatch(setGlobalLoader(true));

        // Generate unique entityId for this new student
        const entityId = new ObjectId().toString();

        try {
            // Clean up empty string values to null
            const cleanedValues = { ...values };
            Object.keys(cleanedValues).forEach(key => {
                if (cleanedValues[key] === '') {
                    cleanedValues[key] = null;
                }
            });

            // Upload profile image
            if (profileImgFile) {
                const compressedFile = await compressImageWithDimension(profileImgFile, 800, 0.85);
                const uploadResult = await s3Upload(compressedFile, {
                    entity: 'student',
                    entityId
                });
                if (uploadResult && uploadResult[0]?.url) {
                    cleanedValues.imageUrl = uploadResult[0].url;
                }
            }

            // Upload learning license image
            if (learningLicenseImgFile) {
                const compressedFile = await compressImageWithDimension(learningLicenseImgFile, 1200, 0.9);
                const uploadResult = await s3Upload(compressedFile, {
                    entity: 'student',
                    entityId
                });
                if (uploadResult && uploadResult[0]?.url) {
                    cleanedValues.learningLicenseImg = uploadResult[0].url;
                }
            }

            // Upload address proof image
            if (addressProofImgFile) {
                const compressedFile = await compressImageWithDimension(addressProofImgFile, 1200, 0.9);
                const uploadResult = await s3Upload(compressedFile, {
                    entity: 'student',
                    entityId
                });
                if (uploadResult && uploadResult[0]?.url) {
                    cleanedValues.addressProofImg = uploadResult[0].url;
                }
            }

            // Upload documents
            if (documents.length > 0) {
                const uploadedDocs = [];
                for (const doc of documents) {
                    const compressedFile = await compressImageWithDimension(doc.file, 1200, 0.9);
                    const uploadResult = await s3Upload(compressedFile, {
                        entity: 'student',
                        entityId
                    });
                    if (uploadResult && uploadResult[0]?.url) {
                        uploadedDocs.push({ name: doc.name, url: uploadResult[0].url });
                    }
                }
                cleanedValues.documents = uploadedDocs;
            }

            await createStudent(cleanedValues);
            toast.success("Student added successfully");
            navigate("/students");
            dispatch(setGlobalLoader(false));
        } catch (error) {
            dispatch(setGlobalLoader(false));
            console.log("create student error - admin", error);
            toast.error(error.response?.data?.msg ?? "some error occured while creating student");
        }
    }

    return (
        <Box>
            <AppBar
                position={'fixed'}
                sx={{
                    backgroundColor: theme.palette.background.paper,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
                elevation={0}
            >
                <Container maxWidth={1} paddingY={{ xs: 1, sm: 1.5 }}>
                    <Topbar onSidebarOpen={handleSidebarOpen} />
                </Container>
            </AppBar>
            <Sidebar
                onClose={handleSidebarClose}
                open={open}
                variant={isMd ? 'permanent' : 'temporary'}
            />
            <main>
                <Box height={{ xs: 94, sm: 102, md: 107 }} />
                <br />
                <br />

                <Box
                    overflow="hidden"
                    marginLeft={{ md: '270px' }}
                    paddingRight={{ xs: 2, md: 4 }}
                >
                    <Typography
                        variant={"h5"}
                        sx={{ marginBottom: 2 }}
                        fontWeight={700}
                    >
                        Add Student
                    </Typography>

                    <Formik
                        initialValues={{
                            name: '',
                            phoneNumber: '',
                            email: '',
                            organizationId: profile === PROFILES.ADMIN ? '' : user?._id || '',
                            address: '',
                            pincode: '',
                            city: '',
                            state: '',
                            guardianName: '',
                            guardianPhoneNumber: '',
                            dob: '',
                            learningLicenseNumber: '',
                            bloodGroup: '',
                        }}

                        validationSchema={validationSchema}
                        onSubmit={(values) => handleOnSubmit(values)}
                        enableReinitialize
                    >
                        {(formikProps) => {
                            const { values, errors, touched, setFieldValue, handleSubmit, isSubmitting } =
                                formikProps;
                            return (
                                <form onSubmit={handleSubmit}>
                                    <Grid container spacing={{ xs: 2, md: 4 }} sx={{ padding: '10px' }}>
                                        {/* Basic Information */}
                                        <Grid item xs={12}>
                                            <Typography variant="h6" fontWeight={600} color="primary">
                                                Basic Information
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 1 }}
                                                fontWeight={700}
                                            >
                                                Name *
                                            </Typography>
                                            <TextField
                                                placeholder="Enter Name"
                                                variant="outlined"
                                                name={"name"}
                                                fullWidth
                                                size="small"
                                                value={values.name}
                                                onChange={(e) =>
                                                    setFieldValue("name", e.target.value)
                                                }
                                                error={
                                                    touched.name && Boolean(errors.name)
                                                }
                                                helperText={touched.name && errors.name}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 1 }}
                                                fontWeight={700}
                                            >
                                                Phone Number *
                                            </Typography>
                                            <TextField
                                                placeholder="Enter Phone Number"
                                                variant="outlined"
                                                name={"phoneNumber"}
                                                fullWidth
                                                size="small"
                                                type="number"
                                                value={values.phoneNumber}
                                                onChange={(e) =>
                                                    e.target.value.toString().length > 10 ?
                                                        setFieldValue("phoneNumber", e.target.value.substring(0, 10))
                                                        :
                                                        setFieldValue("phoneNumber", e.target.value)
                                                }
                                                error={
                                                    touched.phoneNumber && Boolean(errors.phoneNumber)
                                                }
                                                helperText={touched.phoneNumber && errors.phoneNumber}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 1 }}
                                                fontWeight={700}
                                            >
                                                Email
                                            </Typography>
                                            <TextField
                                                placeholder="Enter Email"
                                                variant="outlined"
                                                name={"email"}
                                                fullWidth
                                                size="small"
                                                type="email"
                                                value={values.email}
                                                onChange={(e) =>
                                                    setFieldValue("email", e.target.value)
                                                }
                                                error={
                                                    touched.email && Boolean(errors.email)
                                                }
                                                helperText={touched.email && errors.email}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 1 }}
                                                fontWeight={700}
                                            >
                                                Date of Birth
                                            </Typography>
                                            <TextField
                                                variant="outlined"
                                                name={"dob"}
                                                fullWidth
                                                size="small"
                                                type="date"
                                                value={values.dob}
                                                onChange={(e) =>
                                                    setFieldValue("dob", e.target.value)
                                                }
                                                error={touched.dob && Boolean(errors.dob)}
                                                helperText={touched.dob && errors.dob}
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 1 }}
                                                fontWeight={700}
                                            >
                                                Blood Group
                                            </Typography>
                                            <Select
                                                styles={{
                                                    menu: provided => ({ ...provided, zIndex: 9999 }),
                                                    control: provided => ({
                                                        ...provided,
                                                        minHeight: 40,
                                                        borderColor: touched.bloodGroup && errors.bloodGroup ? '#d32f2f' : provided.borderColor,
                                                    })
                                                }}
                                                placeholder="Select Blood Group"
                                                value={selectedBloodGroup}
                                                options={BLOOD_GROUP_OPTIONS}
                                                isClearable
                                                onChange={(e) => {
                                                    setSelectedBloodGroup(e)
                                                    setFieldValue("bloodGroup", e?.value || '')
                                                }}
                                            />
                                            {touched.bloodGroup && errors.bloodGroup && (
                                                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                                                    {errors.bloodGroup}
                                                </Typography>
                                            )}
                                        </Grid>

                                        {profile === PROFILES.ADMIN &&
                                            <Grid item xs={12} sm={6} md={4}>
                                                <Typography
                                                    variant={"subtitle2"}
                                                    sx={{ marginBottom: 1 }}
                                                    fontWeight={700}
                                                >
                                                    Organization *
                                                </Typography>
                                                <Select
                                                    styles={{
                                                        menu: provided => ({ ...provided, zIndex: 9999 }),
                                                        control: provided => ({ ...provided, minHeight: 40 })
                                                    }}
                                                    placeholder="Select Organization"
                                                    value={selectedOrganization}
                                                    options={organizations}
                                                    onChange={(e) => {
                                                        setSelectedOrganization(e)
                                                        setFieldValue("organizationId", e.value)
                                                    }}
                                                />
                                            </Grid>
                                        }

                                        {/* Profile Image */}
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 1 }}
                                                fontWeight={700}
                                            >
                                                Profile Image
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                                {profileImgPreview && (
                                                    <Box sx={{ position: 'relative' }}>
                                                        <Box
                                                            component="img"
                                                            src={profileImgPreview}
                                                            onClick={() => openImagePreview(profileImgPreview)}
                                                            sx={{
                                                                width: 80,
                                                                height: 80,
                                                                objectFit: 'cover',
                                                                borderRadius: '50%',
                                                                border: '2px solid',
                                                                borderColor: 'divider',
                                                                cursor: 'pointer',
                                                            }}
                                                        />
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleRemoveImage('profile')}
                                                            sx={{
                                                                position: 'absolute',
                                                                top: -4,
                                                                right: -4,
                                                                bgcolor: 'error.main',
                                                                color: 'white',
                                                                '&:hover': { bgcolor: 'error.dark' },
                                                                width: 22,
                                                                height: 22,
                                                            }}
                                                        >
                                                            <CloseIcon sx={{ fontSize: 14 }} />
                                                        </IconButton>
                                                    </Box>
                                                )}
                                                <Box>
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        hidden
                                                        ref={profileImgInputRef}
                                                        onChange={(e) => handleImageSelect(e, 'profile')}
                                                    />
                                                    <Button
                                                        variant="outlined"
                                                        startIcon={<CloudUploadIcon />}
                                                        onClick={() => profileImgInputRef.current?.click()}
                                                        size="small"
                                                    >
                                                        {profileImgPreview ? 'Change' : 'Upload'}
                                                    </Button>
                                                    <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary' }}>
                                                        Max: 5MB
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Grid>

                                        {/* Guardian Information */}
                                        <Grid item xs={12} sx={{ mt: 2 }}>
                                            <Typography variant="h6" fontWeight={600} color="primary">
                                                Guardian Information
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 1 }}
                                                fontWeight={700}
                                            >
                                                Guardian Name
                                            </Typography>
                                            <TextField
                                                placeholder="Enter Guardian Name"
                                                variant="outlined"
                                                name={"guardianName"}
                                                fullWidth
                                                size="small"
                                                value={values.guardianName}
                                                onChange={(e) =>
                                                    setFieldValue("guardianName", e.target.value)
                                                }
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 1 }}
                                                fontWeight={700}
                                            >
                                                Guardian Phone Number
                                            </Typography>
                                            <TextField
                                                placeholder="Enter Guardian Phone Number"
                                                variant="outlined"
                                                name={"guardianPhoneNumber"}
                                                fullWidth
                                                size="small"
                                                type="number"
                                                value={values.guardianPhoneNumber}
                                                onChange={(e) =>
                                                    e.target.value.toString().length > 10 ?
                                                        setFieldValue("guardianPhoneNumber", e.target.value.substring(0, 10))
                                                        :
                                                        setFieldValue("guardianPhoneNumber", e.target.value)
                                                }
                                                error={
                                                    touched.guardianPhoneNumber && Boolean(errors.guardianPhoneNumber)
                                                }
                                                helperText={touched.guardianPhoneNumber && errors.guardianPhoneNumber}
                                            />
                                        </Grid>

                                        {/* Address Information */}
                                        <Grid item xs={12} sx={{ mt: 2 }}>
                                            <Typography variant="h6" fontWeight={600} color="primary">
                                                Address Information
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={8}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 1 }}
                                                fontWeight={700}
                                            >
                                                Address
                                            </Typography>
                                            <TextField
                                                placeholder="Enter Address"
                                                variant="outlined"
                                                name={"address"}
                                                fullWidth
                                                size="small"
                                                value={values.address}
                                                onChange={(e) =>
                                                    setFieldValue("address", e.target.value)
                                                }
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 1 }}
                                                fontWeight={700}
                                            >
                                                State
                                            </Typography>
                                            <Select
                                                styles={{
                                                    menu: provided => ({ ...provided, zIndex: 9999 }),
                                                    control: provided => ({ ...provided, minHeight: 40 })
                                                }}
                                                placeholder="Select State"
                                                value={selectedState}
                                                options={stateOptions}
                                                isClearable
                                                onChange={(e) => {
                                                    setSelectedState(e);
                                                    setFieldValue("state", e?.value || '');
                                                    setFieldValue("city", '');
                                                }}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 1 }}
                                                fontWeight={700}
                                            >
                                                City
                                            </Typography>
                                            <Select
                                                styles={{
                                                    menu: provided => ({ ...provided, zIndex: 9999 }),
                                                    control: provided => ({ ...provided, minHeight: 40 })
                                                }}
                                                placeholder="Select City"
                                                value={selectedCity}
                                                options={cityOptions}
                                                isClearable
                                                isDisabled={!selectedState}
                                                onChange={(e) => {
                                                    setSelectedCity(e);
                                                    setFieldValue("city", e?.value || '');
                                                }}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 1 }}
                                                fontWeight={700}
                                            >
                                                Pincode
                                            </Typography>
                                            <TextField
                                                placeholder="Enter Pincode"
                                                variant="outlined"
                                                name={"pincode"}
                                                fullWidth
                                                size="small"
                                                type="number"
                                                value={values.pincode}
                                                onChange={(e) => {
                                                    const val = e.target.value.toString();
                                                    if (val.length <= 6) {
                                                        setFieldValue("pincode", val);
                                                    }
                                                }}
                                                inputProps={{ maxLength: 6 }}
                                            />
                                        </Grid>

                                        {/* License & Documents */}
                                        <Grid item xs={12} sx={{ mt: 2 }}>
                                            <Typography variant="h6" fontWeight={600} color="primary">
                                                License & Documents
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 1 }}
                                                fontWeight={700}
                                            >
                                                Learning License Number
                                            </Typography>
                                            <TextField
                                                placeholder="Enter Learning License Number"
                                                variant="outlined"
                                                name={"learningLicenseNumber"}
                                                fullWidth
                                                size="small"
                                                value={values.learningLicenseNumber}
                                                onChange={(e) =>
                                                    setFieldValue("learningLicenseNumber", e.target.value)
                                                }
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 1 }}
                                                fontWeight={700}
                                            >
                                                Learning License Image
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                                {learningLicenseImgPreview && (
                                                    <Box sx={{ position: 'relative' }}>
                                                        <Box
                                                            component="img"
                                                            src={learningLicenseImgPreview}
                                                            onClick={() => openImagePreview(learningLicenseImgPreview)}
                                                            sx={{
                                                                width: 100,
                                                                height: 70,
                                                                objectFit: 'cover',
                                                                borderRadius: 1,
                                                                border: '2px solid',
                                                                borderColor: 'divider',
                                                                cursor: 'pointer',
                                                            }}
                                                        />
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleRemoveImage('learningLicense')}
                                                            sx={{
                                                                position: 'absolute',
                                                                top: -8,
                                                                right: -8,
                                                                bgcolor: 'error.main',
                                                                color: 'white',
                                                                '&:hover': { bgcolor: 'error.dark' },
                                                            }}
                                                        >
                                                            <CloseIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                )}
                                                <Box>
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        hidden
                                                        ref={learningLicenseInputRef}
                                                        onChange={(e) => handleImageSelect(e, 'learningLicense')}
                                                    />
                                                    <Button
                                                        variant="outlined"
                                                        startIcon={<CloudUploadIcon />}
                                                        onClick={() => learningLicenseInputRef.current?.click()}
                                                        size="small"
                                                    >
                                                        {learningLicenseImgPreview ? 'Change' : 'Upload'}
                                                    </Button>
                                                    <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary' }}>
                                                        Max: 5MB (JPEG, PNG, WebP)
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 1 }}
                                                fontWeight={700}
                                            >
                                                Address Proof Image
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                                {addressProofImgPreview && (
                                                    <Box sx={{ position: 'relative' }}>
                                                        <Box
                                                            component="img"
                                                            src={addressProofImgPreview}
                                                            onClick={() => openImagePreview(addressProofImgPreview)}
                                                            sx={{
                                                                width: 100,
                                                                height: 70,
                                                                objectFit: 'cover',
                                                                borderRadius: 1,
                                                                border: '2px solid',
                                                                borderColor: 'divider',
                                                                cursor: 'pointer',
                                                            }}
                                                        />
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleRemoveImage('addressProof')}
                                                            sx={{
                                                                position: 'absolute',
                                                                top: -8,
                                                                right: -8,
                                                                bgcolor: 'error.main',
                                                                color: 'white',
                                                                '&:hover': { bgcolor: 'error.dark' },
                                                            }}
                                                        >
                                                            <CloseIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                )}
                                                <Box>
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        hidden
                                                        ref={addressProofInputRef}
                                                        onChange={(e) => handleImageSelect(e, 'addressProof')}
                                                    />
                                                    <Button
                                                        variant="outlined"
                                                        startIcon={<CloudUploadIcon />}
                                                        onClick={() => addressProofInputRef.current?.click()}
                                                        size="small"
                                                    >
                                                        {addressProofImgPreview ? 'Change' : 'Upload'}
                                                    </Button>
                                                    <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary' }}>
                                                        Max: 5MB (JPEG, PNG, WebP)
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Grid>

                                        {/* Additional Documents */}
                                        <Grid item xs={12} sx={{ mt: 2 }}>
                                            <Typography variant="h6" fontWeight={600} color="primary">
                                                Additional Documents
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={4} md={3}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 1 }}
                                                fontWeight={700}
                                            >
                                                Document Name
                                            </Typography>
                                            <TextField
                                                placeholder="e.g., 10th Memo"
                                                variant="outlined"
                                                fullWidth
                                                size="small"
                                                value={newDocName}
                                                onChange={(e) => setNewDocName(e.target.value)}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={4} md={4}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 1 }}
                                                fontWeight={700}
                                            >
                                                Document Image
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                {newDocPreview && (
                                                    <Box
                                                        component="img"
                                                        src={newDocPreview}
                                                        onClick={() => openImagePreview(newDocPreview)}
                                                        sx={{
                                                            width: 60,
                                                            height: 45,
                                                            objectFit: 'cover',
                                                            borderRadius: 1,
                                                            border: '1px solid',
                                                            borderColor: 'divider',
                                                            cursor: 'pointer',
                                                        }}
                                                    />
                                                )}
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    hidden
                                                    ref={documentInputRef}
                                                    onChange={(e) => handleImageSelect(e, 'document')}
                                                />
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<CloudUploadIcon />}
                                                    onClick={() => documentInputRef.current?.click()}
                                                    size="small"
                                                >
                                                    {newDocPreview ? 'Change' : 'Select'}
                                                </Button>
                                            </Box>
                                        </Grid>

                                        <Grid item xs={12} sm={4} md={2} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                                            <Button
                                                variant="contained"
                                                startIcon={<AddIcon />}
                                                onClick={handleAddDocument}
                                                size="medium"
                                                disabled={!newDocName.trim() || !newDocFile}
                                            >
                                                Add
                                            </Button>
                                        </Grid>

                                        {documents.length > 0 && (
                                            <Grid item xs={12}>
                                                <Box sx={{ mt: 1 }}>
                                                    {documents.map((doc, index) => (
                                                        <Box
                                                            key={index}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 2,
                                                                mb: 1,
                                                                p: 1,
                                                                bgcolor: 'grey.100',
                                                                borderRadius: 1
                                                            }}
                                                        >
                                                            {doc.preview && (
                                                                <Box
                                                                    component="img"
                                                                    src={doc.preview}
                                                                    onClick={() => openImagePreview(doc.preview)}
                                                                    sx={{
                                                                        width: 50,
                                                                        height: 35,
                                                                        objectFit: 'cover',
                                                                        borderRadius: 1,
                                                                        cursor: 'pointer',
                                                                    }}
                                                                />
                                                            )}
                                                            <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                                                                {doc.name}
                                                            </Typography>
                                                            <IconButton size="small" onClick={() => handleRemoveDocument(index)} color="error">
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Grid>
                                        )}

                                    </Grid>
                                    <br />
                                    <Button
                                        component={'button'}
                                        variant={"contained"}
                                        fontWeight={700}
                                        type='submit'
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? <CircularProgress size={24} /> : "Submit"}
                                    </Button>
                                    <br />
                                    <br />

                                </form>
                            )
                        }}
                    </Formik>
                </Box>
            </main>

            {/* Image Preview Dialog */}
            <Dialog
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogContent sx={{ p: 1, position: 'relative' }}>
                    <IconButton
                        onClick={() => setPreviewOpen(false)}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: 'rgba(0,0,0,0.5)',
                            color: 'white',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <Box
                        component="img"
                        src={previewImage}
                        sx={{
                            width: '100%',
                            height: 'auto',
                            maxHeight: '80vh',
                            objectFit: 'contain',
                        }}
                    />
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default AddStudent;
